/**
 * Payment processing fees passed to the client. Pure — shared by edge
 * functions and the CRM.
 *
 * The fee is grossed up so the organization nets the invoice subtotal after
 * Stripe takes its cut of the total charge:
 * - card: 2.9% + $0.30
 * - bank (ACH Direct Debit): 0.8%, capped at $5
 */

/** How a client may pay: set on the organization, account/contact, invoice. */
export type ClientPaymentMethod = "card" | "bank" | "card_or_bank";
/** How one payment is made (decides the fee). */
export type ChargeMethod = "card" | "bank";

export const CLIENT_PAYMENT_METHODS: ReadonlyArray<{
  id: ClientPaymentMethod;
  name: string;
}> = [
  { id: "card", name: "Card" },
  { id: "bank", name: "Bank (ACH)" },
  { id: "card_or_bank", name: "Card or bank" },
];

export const CARD_FEE_RATE = 0.029;
export const CARD_FEE_FIXED = 0.3;
export const BANK_FEE_RATE = 0.008;
export const BANK_FEE_CAP = 5;

export const PROCESSING_FEE_LABELS: Record<ChargeMethod, string> = {
  card: "Card processing fee (2.9% + $0.30)",
  bank: "Bank processing fee (0.8%, max $5)",
};

/** Short rate text shown next to "Processing fee". */
export const PROCESSING_FEE_RATES: Record<ChargeMethod, string> = {
  card: "2.9% + $0.30",
  bank: "0.8%, max $5",
};

/** Rate text for an invoice's fee basis (defaults to card). */
export const processingFeeRateLabel = (feeMethod?: string | null) =>
  PROCESSING_FEE_RATES[feeMethod === "bank" ? "bank" : "card"];

const round = (value: number) => Math.round(value * 100) / 100;

export const isClientPaymentMethod = (
  value: unknown,
): value is ClientPaymentMethod =>
  value === "card" || value === "bank" || value === "card_or_bank";

/** Fee to add to `subtotal` so the organization nets `subtotal`. */
export const processingFeeFor = (subtotal: number, method: ChargeMethod) => {
  if (!(subtotal > 0)) return 0;
  if (method === "bank") {
    const fee = subtotal / (1 - BANK_FEE_RATE) - subtotal;
    return round(Math.min(fee, BANK_FEE_CAP));
  }
  return round((subtotal + CARD_FEE_FIXED) / (1 - CARD_FEE_RATE) - subtotal);
};

/** Fee basis an invoice starts with: bank only for bank-only clients. */
export const defaultFeeMethod = (method: ClientPaymentMethod): ChargeMethod =>
  method === "bank" ? "bank" : "card";

/** Methods the pay page offers for an invoice. */
export const allowedChargeMethods = (
  method: ClientPaymentMethod,
): ChargeMethod[] =>
  method === "card_or_bank" ? ["card", "bank"] : [defaultFeeMethod(method)];

/** Stripe payment_method_types for a charge method. */
export const stripePaymentMethodTypes = (method: ChargeMethod) =>
  method === "bank" ? ["us_bank_account"] : ["card"];

/** Charge method of a Stripe payment method type. */
export const chargeMethodForStripeType = (
  type: string | null | undefined,
): ChargeMethod => (type === "us_bank_account" ? "bank" : "card");

/**
 * Client's method: account, else contact, else the organization default.
 */
export const resolveClientPaymentMethod = (params: {
  companyMethod?: string | null;
  contactMethod?: string | null;
  orgDefault?: string | null;
}): ClientPaymentMethod =>
  [params.companyMethod, params.contactMethod, params.orgDefault].find(
    isClientPaymentMethod,
  ) ?? "card";

/**
 * Amount and fee after switching the fee basis of an invoice. `amount` and
 * `feeAmount` are the current values; the base (amount − fee) stays.
 */
export const repriceForChargeMethod = (params: {
  amount: number;
  feeAmount: number;
  method: ChargeMethod;
}) => {
  const base = round(
    (Number(params.amount) || 0) - (Number(params.feeAmount) || 0),
  );
  const feeAmount = processingFeeFor(base, params.method);
  return { base, feeAmount, amount: round(base + feeAmount) };
};
